import { useEffect, useRef, useState } from 'react';

import { Group, Image as KonvaImage, Rect } from 'react-konva';

import {
  getBackgroundRenderedSize,
  normalizeBackgroundReferenceImage,
} from '../lib/floorPlanBackgroundImageState.js';

/** @type {Map<string, HTMLImageElement>} */
const imageCache = new Map();

/**
 * @param {string} source
 * @returns {Promise<HTMLImageElement>}
 */
function loadCachedImage(source) {
  const cached = imageCache.get(source);
  if (cached && cached.complete && cached.naturalWidth > 0) {
    return Promise.resolve(cached);
  }

  return new Promise((resolve, reject) => {
    const img = cached || new window.Image();
    const finish = () => {
      imageCache.set(source, img);
      resolve(img);
    };
    img.onload = finish;
    img.onerror = reject;
    if (img.src !== source) {
      img.src = source;
    } else if (img.complete && img.naturalWidth > 0) {
      finish();
    }
  });
}

/**
 * @param {{
 *   background: import('../lib/floorPlanBackgroundImageState.js').BackgroundReferenceImage;
 *   metersPerPixel: number;
 *   displayScale: number;
 *   editMode: boolean;
 *   onTransform: (patch: Partial<import('../lib/floorPlanBackgroundImageState.js').BackgroundReferenceImage>) => void;
 * }} props
 */
function FloorPlanBackgroundImageLayer({
  background,
  metersPerPixel,
  displayScale,
  editMode,
  onTransform,
}) {
  const [image, setImage] = useState(/** @type {HTMLImageElement | null} */ (null));
  const [position, setPosition] = useState(() => ({
    x: background.worldX,
    y: background.worldY,
  }));
  const isDraggingRef = useRef(false);

  useEffect(() => {
    if (!background?.source) {
      setImage(null);
      return undefined;
    }

    let cancelled = false;
    loadCachedImage(background.source)
      .then((img) => {
        if (!cancelled) setImage(img);
      })
      .catch(() => {
        if (!cancelled) setImage(null);
      });

    return () => {
      cancelled = true;
    };
  }, [background?.source]);

  useEffect(() => {
    if (isDraggingRef.current) return;
    const normalized = normalizeBackgroundReferenceImage(background, metersPerPixel);
    if (!normalized) return;
    setPosition({ x: normalized.worldX, y: normalized.worldY });
  }, [background, background.worldX, background.worldY, metersPerPixel]);

  const normalizedBackground = normalizeBackgroundReferenceImage(background, metersPerPixel);

  if (!normalizedBackground?.visible || !image) return null;

  const { widthMeters, heightMeters } = getBackgroundRenderedSize(normalizedBackground);
  const widthPx = widthMeters / metersPerPixel;
  const heightPx = heightMeters / metersPerPixel;
  const offsetX = widthPx / 2;
  const offsetY = heightPx / 2;

  return (
    <Group
      x={position.x}
      y={position.y}
      offsetX={offsetX}
      offsetY={offsetY}
      rotation={normalizedBackground.rotationDegrees}
      opacity={normalizedBackground.opacity}
      listening={editMode}
      draggable={editMode}
      onDragStart={() => {
        isDraggingRef.current = true;
      }}
      onDragEnd={(e) => {
        isDraggingRef.current = false;
        const worldX = e.target.x();
        const worldY = e.target.y();
        if (!Number.isFinite(worldX) || !Number.isFinite(worldY)) return;
        setPosition({ x: worldX, y: worldY });
        onTransform({ worldX, worldY });
      }}
    >
      <KonvaImage image={image} width={widthPx} height={heightPx} listening={false} />
      {editMode ? (
        <Rect
          x={0}
          y={0}
          width={widthPx}
          height={heightPx}
          stroke="#66D98A"
          strokeWidth={1.5 / displayScale}
          dash={[6 / displayScale, 4 / displayScale]}
          listening={false}
        />
      ) : null}
    </Group>
  );
}

export default FloorPlanBackgroundImageLayer;
